/**
 * Prefixes every console message with a wall-clock timestamp, so we can see timing in device logs
 * (Xcode / logcat) where there's no timing column.
 *
 * We install the prefix using a property getter that returns a freshly _bound_ native console method,
 * rather than a wrapper function, so the browser's devtools still attribute each message to the line
 * that logged it rather than to this file.
 */

const METHODS = ['debug', 'log', 'info', 'warn', 'error'] as const

type ConsoleMethod = typeof console.log

function timestamp(): string {
	const now = new Date()
	const time = now.toTimeString().substring(0, 8)
	const millis = `${now.getMilliseconds()}`.padStart(3, '0')
	return `${time}.${millis}`
}

export function installConsoleTimestamps(): void {
	for (const method of METHODS) {
		let target: ConsoleMethod = console[method]
		if (typeof target !== 'function') {
			continue
		}

		Object.defineProperty(console, method, {
			configurable: true,
			get() {
				return target.bind(console, timestamp())
			},
			/*
			 * A getter without a setter makes the property readonly, and libraries that wrap the
			 * console do so by assigning to it (Sentry's `fill()`, for one). In a module, that
			 * assignment is strict-mode and throws, which is fatal at load. So accept the wrapper
			 * and prefix it instead, leaving console patching working as the library expects.
			 */
			set(value: ConsoleMethod) {
				target = value
			},
		})
	}
}
